import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';

import { ToastService } from '@brightside/brightside-ui-services';
import { MenuItem } from '@brightside/brightside-ui';
import { Environment } from '@micro-core/environment';
import { PageTransitionAnimationTriggers } from '@micro-ui/template/page';

import {
  FirebaseService,
  LinkedAccountRequestBody,
  LinkedAccountResponse,
  LinkedAccountType,
} from '@brightside-web/desktop/data-access/shared';
import { LinkedAccountService } from '@brightside-web/desktop/data-access/account-linking';

import { Subscription } from 'rxjs';
import { BsHubService } from '@brightside-web/desktop/data-access/core-services';
import moment from 'moment-business-days';
import { TranslateService } from '@ngx-translate/core';

export const luhnValidator =
  (): ValidatorFn =>
  (control: AbstractControl): { [key: string]: any } | null => {
    const luhnFail = !_passesLuhnCheck(control.value);
    return luhnFail ? { luhnFail: { value: control.value } } : null;
  };

const _passesLuhnCheck = (routing: string) => {
  const checksumTotal =
    7 * (parseInt(routing.charAt(0), 10) + parseInt(routing.charAt(3), 10) + parseInt(routing.charAt(6), 10)) +
    3 * (parseInt(routing.charAt(1), 10) + parseInt(routing.charAt(4), 10) + parseInt(routing.charAt(7), 10)) +
    9 * (parseInt(routing.charAt(2), 10) + parseInt(routing.charAt(5), 10) + parseInt(routing.charAt(8), 10));
  const checksumMod = checksumTotal % 10;
  return checksumMod === 0;
};

@Component({
    animations: PageTransitionAnimationTriggers,
    selector: 'brightside-web-account-add',
    templateUrl: './add.component.html',
    styleUrls: ['./add.component.scss'],
    standalone: false
})
export class AccountAddComponent implements OnInit {
  // ToDo: should have an object passed in that can have all these configs - maybe
  @Input() pageStepperActiveIndex = -1;
  @Input() pageOnBackCtaPath: string[] = [];
  @Input() pageOnForwardCtaPath: string[] = [];
  @Input() pageOnBackwardCtaPath: string[] = [];

  @Input() fbPage = '';
  @Input() fbCategory = '';
  @Input() fbEventName = '';

  protected sub = new Subscription();

  accountTypes :{name:string; code:string}[];

  selectConfig = {};
  selectAccountTypesConfig = {};

  routingForm = new FormGroup(
    {
      routingNumber: new FormControl('', [Validators.required, Validators.pattern('[012346789][0-9]{8}'), luhnValidator()]),
      accountNumber: new FormControl('', [Validators.required, Validators.pattern('\\d{5,25}')]),
      accountNumberConfirm: new FormControl('', [Validators.required, Validators.pattern('\\d{5,25}')]),
      accountType: new FormControl('', [Validators.required]),
    },
    { validators: [] }
  );

  pageTitle :string;
  pageSubTitle :string;
  pageCtaLabel :string;
  pageSecondaryCtaLabel = '';
  pageStepperItems: MenuItem[] = [{}, {}, {}, {}];
  pageHideBackButton = false;

  microTitle :string;
  microLineWithDate = '';
  microPrimaryCta :string;

  processing = false;
  fatalError = false;

  isCtaDisabled = true;

  showMicroWarning = false;
  shouldReverseExitAnimation = false;

  constructor(
    protected analytics: FirebaseService,
    private changeDetectorRef: ChangeDetectorRef,
    private env: Environment,
    private router: Router,
    private toastService: ToastService,
    private linkedAccountService: LinkedAccountService,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private bsHubService: BsHubService
  ) {
    const data = this.activatedRoute.snapshot.data;

    this.pageStepperActiveIndex = data.pageStepperActiveIndex || -1;
    this.pageOnBackwardCtaPath = data.pageOnBackwardCtaPath || [];
    this.pageOnForwardCtaPath = data.pageOnForwardCtaPath || [];
    this.pageHideBackButton = data.pageHideBackButton || false;

    if (data.pageSecondaryCtaLabel) {
      this.pageSecondaryCtaLabel = data.pageSecondaryCtaLabel;
    } else {
      this.pageSecondaryCtaLabel = this.pageStepperActiveIndex > 0 ? 'MICRO_ACCOUNTS_ADD_PAGE_SECONDARY_CTA' : '';
    }

    this.fbPage = data.fbPage;
    this.fbCategory = data.fbCategory;
    this.fbEventName = data.fbEventName;

    this.pageTitle = this.translateService.instant('MICRO_ADD_ACCOUNT_TITLE');
    this.pageSubTitle = this.translateService.instant('MICRO_ADD_ACCOUNT_SUB_TITLE');
    this.pageCtaLabel = this.translateService.instant('MICRO_ADD_ACCOUNT_CTA');
    this.microTitle = this.translateService.instant('MICRO_ADD_ACCOUNT_MICRO_TITLE');
    this.microPrimaryCta = this.translateService.instant('MICRO_ADD_ACCOUNT_MICRO_PRIMARY_CTA');
    this.accountTypes = [
      { name: this.translateService.instant('MICRO_ADD_ACCOUNT_CHECKING_NAME'), code: LinkedAccountType.CHECKING },
      { name: this.translateService.instant('MICRO_ADD_ACCOUNT_SAVINGS_NAME'), code: LinkedAccountType.SAVINGS },
    ];
  }

  ngOnInit() {
    this.setUpHub();

    this.routingForm.valueChanges.subscribe(() => {
      // Need to check if all values are ready
      this.checkIsFormComplete();
    });

    this.translateService.get('SELECT_ACCOUNT').subscribe((labelText: string) => {
      this.selectConfig = {
        displayKey: 'name',
        clearOnSelection: true,
        placeholder: labelText,
      };

      this.selectAccountTypesConfig = {
        ...this.selectConfig,
        placeholder: this.translateService.instant('MICRO_ADD_ACCOUNT_ACCOUNT_TYPES_PLACEHOLDER'),
      };
    });
  }

  get accountType() {
    return this.routingForm.get('accountType');
  }
  get routingNumber() {
    return this.routingForm.get('routingNumber');
  }
  get accountNumber() {
    return this.routingForm.get('accountNumber');
  }
  get accountNumberConfirm() {
    return this.routingForm.get('accountNumberConfirm');
  }

  public isValidEntry(field: AbstractControl | null) {
    return field && field.value.length > 0 && !field.errors;
  }

  public checkIsFormComplete() {
    //todo this really should all be done with form validators, whatever
    this.isCtaDisabled =
      this.accountType?.value &&
      this.isValidEntry(this.accountNumber) &&
      this.isValidEntry(this.accountNumberConfirm) &&
      this.isValidEntry(this.routingNumber) &&
      this.accountNumber?.value === this.accountNumberConfirm?.value
        ? false
        : true;
  }

  private setUpHub() {
    this.bsHubService.listen('ErrorChannel', () => {
      this.createLinkedAccount();
    });
  }

  private getTwoBusinessDays() {
    return moment(new Date()).local().businessAdd(2).startOf('day');
  }

  private getRequestBody(): LinkedAccountRequestBody {
    return {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      account_class: this.accountType?.value?.code || LinkedAccountType.SAVINGS,
      account_num: this.accountNumber?.value || '',
      routing_num: this.routingNumber?.value || '',
    };
  }

  private handleSuccessOnLinkedAccount(response: LinkedAccountResponse) {
    // If the account is linked completely move forward
    if (response.linkedCompletely) {
      this.router.navigate(this.pageOnForwardCtaPath);
      return;
    }

    this.microLineWithDate = this.translateService.instant('MICRO_ADD_ACCOUNT_CHECKING_NAME', {date: this.getTwoBusinessDays().format('MMM Do')});
    this.showMicroWarning = true;

    this.processing = false;
    this.fatalError = false;
  }

  private handleFailureOnLinkedAccount() {
    this.processing = false;
    this.fatalError = true;
    this.analytics.logEvent('error_shown', { 'error id': 'create_linked_account' });
    this.toastService.error(this.translateService.instant('MICRO_ADD_ACCOUNT_TOAST_ERROR'), { link: true, transient: false });
  }

  private createLinkedAccount() {
    this.processing = true;
    this.linkedAccountService.createAccount(this.getRequestBody()).subscribe(
      (response: LinkedAccountResponse) => this.handleSuccessOnLinkedAccount(response),
      () => this.handleFailureOnLinkedAccount()
    );
  }

  handleBackCtaClick() {
    this.shouldReverseExitAnimation = true;
    this.changeDetectorRef.detectChanges();

    this.handleSecondaryCtaClick();
  }

  handleSecondaryCtaClick() {
    this.router.navigate(this.pageOnBackwardCtaPath.length > 0 ? this.pageOnBackwardCtaPath : this.pageOnForwardCtaPath);
  }

  handleCtaClick() {
    if (this.processing || this.isCtaDisabled) {
      return;
    }

    this.createLinkedAccount();
  }

  handleMicroCtaClick() {
    this.router.navigate(this.pageOnForwardCtaPath);
  }
}
